import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View } from './components/base_components';

import { BusyOverlay, useBusy } from './components/busy_component';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import StandardScreen from './components/standard_screen';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';

import OrgStore from './stores/org_store';
import UserStore from './stores/user_store';

import { byteFormat } from './tools/util';

const SUFFIX = process.env.CUSTOM_DOMAIN_SUFFIX;

const styles = StyleSheet.create({
  orgScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
  spinner: {
    margin: '3rem',
    alignSelf: 'center',
  },
});

export default function OrgScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org_id = parseInt(params.org_id ?? '0', 10) || 0;
  const org = OrgStore.useOrg(org_id);
  const login_custom_domain = UserStore.getCustomDomain();
  const [updates, setUpdates] = React.useState<any>({});
  const [selected, setSelected] = React.useState<number[]>([]);
  const [new_s3_bucket, setNewS3Bucket] = React.useState<any>({});
  const selected_bucket = org?.s3_bucket_list?.[selected?.[0]];
  const bucket_count = org?.s3_bucket_list?.length;
  const custom_domain = `https://${org?.domain_prefix}${SUFFIX}`;

  function _setUpdates(new_updates) {
    if (new_updates.domain_prefix) {
      new_updates.domain_prefix = new_updates.domain_prefix.toLowerCase();
    }
    setUpdates(new_updates);
  }

  React.useEffect(() => {
    OrgStore.fetch(org_id);
  }, [org_id]);

  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = { org_id };
      if (updates.org_name !== undefined) {
        opts.org_name = updates.org_name;
      }
      if (updates.domain_prefix !== undefined) {
        opts.domain_prefix = updates.domain_prefix;
      }
      OrgStore.updateOrg(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert('Name in use, please try another.');
        } else if (err === 412) {
          window.alert('Name reserved, please try another.');
        } else if (err === 400) {
          window.alert(
            'Invalid domain prefix, please use a-z, 0-9, dash and underscore only.'
          );
        } else if (err) {
          window.alert('Failed to update org.');
        } else {
          complete?.();
          if (opts.domain_prefix !== undefined && login_custom_domain) {
            if (opts.domain_prefix) {
              window.alert(
                'Org saved, you will be redirected to the new domain to login.'
              );
              const url = `https://${opts.domain_prefix}${SUFFIX}`;
              window.location.href = url;
            } else {
              window.alert(
                'Org saved and custom domain removed. You will be redirected to Maxlo login.'
              );
              window.location.href = process.env.BASE_URL ?? '';
            }
          }
        }
      });
    }
  }
  function _onCreateBucket(complete) {
    const opts = Object.assign({ org_id }, new_s3_bucket);
    opts.max_size = parseFloat(opts.max_size);
    if (setBusy()) {
      OrgStore.addBucket(opts, (err) => {
        if (err === 412) {
          window.alert(
            'Failed to validate bucket, please check your settings.'
          );
        } else if (err) {
          window.alert('Failed to add bucket, please try again.');
        } else {
          complete(null);
        }
        clearBusy();
      });
    }
  }
  function _flipBuckets(a, b, success) {
    if (a && b && setBusy()) {
      const update_list = [
        { s3_bucket_id: a.s3_bucket_id, priority: b.priority },
        { s3_bucket_id: b.s3_bucket_id, priority: a.priority },
      ];
      const opts = { org_id, update_list };
      OrgStore.orderBuckets(opts, (err) => {
        if (err) {
          window.alert('Failed to reorder, please try again.');
        } else {
          success();
        }
        clearBusy();
      });
    }
  }
  function _onMoveUpPress() {
    const previous_index = selected?.[0] - 1;
    const previous = org?.s3_bucket_list?.[previous_index];
    _flipBuckets(previous, selected_bucket, () => {
      setSelected([previous_index]);
    });
  }
  function _onMoveDownPress() {
    const next_index = selected?.[0] + 1;
    const next = org?.s3_bucket_list?.[next_index];
    _flipBuckets(next, selected_bucket, () => {
      setSelected([next_index]);
    });
  }

  const buttons = (
    <>
      <TextButton
        style={styles.actionButton}
        text='Move Up'
        disabled={!selected_bucket || selected[0] === 0}
        onPress={_onMoveUpPress}
      />
      <TextButton
        style={styles.actionButton}
        text='Move Down'
        disabled={!selected_bucket || selected[0] === bucket_count - 1}
        onPress={_onMoveDownPress}
      />
    </>
  );

  const rows = org?.s3_bucket_list?.map?.((bucket, i) => ({
    url: `/org/${org_id}/bucket/${bucket.s3_bucket_id}`,
    list: [
      bucket.s3_bucket,
      bucket.max_size ? byteFormat(bucket.max_size) : '-',
      bucket.priority ? i + 1 : '-',
      bucket.is_disabled ? 'Disabled' : 'Enabled',
    ],
  }));
  let content: any = null;
  if (!org) {
    content = <ProgressSpinner style={styles.spinner} color='#555' />;
  } else {
    content = (
      <>
        <TitleActionLine title={org?.org_name} />
        <BoxPropList
          title='Organization Details'
          editable={true}
          updates={updates}
          onChange={_setUpdates}
          onSave={_onSave}
          columns={[
            [
              {
                key: 'org_name',
                label: 'Organization Name',
                value: org?.org_name,
                input: {
                  placeholder: 'org name',
                },
              },
              {
                key: 'domain_prefix',
                label: 'Custom Domain',
                value: org?.domain_prefix,
                valuePrefix: 'https://',
                valueSuffix: SUFFIX,
                url: org?.domain_prefix ? custom_domain : null,
                emptyValue: '-',
                input: {
                  placeholder: 'domain',
                  suffix: SUFFIX,
                },
              },
            ],
          ]}
        />
        <StandardBoxList
          title='Network Stores'
          headers={['Name', 'Max File Size', 'Priority', 'Status']}
          buttons={buttons}
          rows={rows}
          singleSelect={true}
          selected={selected}
          onChange={setSelected}
        />
        <BoxPropList
          title='Add Network Store'
          editable={true}
          alwaysEdit={true}
          updates={new_s3_bucket}
          onChange={setNewS3Bucket}
          onSave={_onCreateBucket}
          columns={[
            [
              {
                key: 's3_bucket',
                label: 'Bucket Name',
                value: '',
                input: {
                  placeholder: 'bucket',
                },
                required: true,
              },
              {
                key: 's3_bucket_region',
                label: 'S3 Region',
                value: '',
                input: {
                  placeholder: 'region',
                },
                required: true,
              },
              {
                key: 's3_host',
                label: 'S3 Host',
                value: '',
                input: {
                  placeholder: 'host',
                },
                required: true,
              },
              {
                key: 's3_bucket_prefix',
                label: 'S3 Bucket Prefix',
                value: '',
                input: {
                  placeholder: 'prefix (optional)',
                },
              },
              {
                key: 'aws_access_key_id',
                label: 'Access Key ID',
                value: '',
                input: {
                  placeholder: 'access key id',
                },
                required: true,
              },
              {
                key: 'aws_secret_access_key',
                label: 'Secret Access Key',
                value: '',
                input: {
                  placeholder: 'secret access key',
                },
                required: true,
              },
              {
                key: 'max_size',
                label: 'Max Object Size',
                value: '4398046511104',
                input: {
                  placeholder: 'bytes',
                  type: 'number',
                },
                required: true,
              },
            ],
          ]}
        />
      </>
    );
  }
  return (
    <View style={styles.orgScreen}>
      <StandardScreen>{content}</StandardScreen>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
